/**
 * OPTIMIZED SPLIDE MANAGER FOR ELLA THEME - FINAL FIX V5
 * - **FIXED: Uncaught ReferenceError by moving reinitSplides and handleInfiniteScrollComplete to main scope.**
 * - Includes previous fixes for correct selectors and pagination replacement.
 */

(function () {
  'use strict';

  /* ----------------------------------------------
   * CONFIGURATION & UTILITIES
   * ---------------------------------------------- */
  const CARD_SELECTOR = '.card-media-splide';
  const COLLECTION_SLIDER_SELECTOR = '.luis-collection-slider-splide';
  const DEBOUNCE_TIME = 300;

  // 🚨 CORRECTED SELECTORS BASED ON PROVIDED LIQUID CODE
  const PRODUCT_GRID_CONTAINER_SELECTOR = '#main-collection-product-grid'; 
  const PRODUCT_ITEM_SELECTOR = 'li.product'; 
  const PAGINATION_WRAPPER_SELECTOR = '.pagination-wrapper';
  // -----------------------------------------------------------------------


  const instances = new Map();
  let infiniteScrollObserver = null;
  let filterButtonInitialized = false; 
  let isFetchingNextPage = false; // Flag to prevent multiple concurrent loads

  // Faster debounce for better responsiveness
  const debounce = (fn, wait = 100) => {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  // Safe destroy with better cleanup
  function safeDestroy(el) {
    if (!el) return;
    const instanceId = el.id;
    const instance = instances.get(instanceId);
    if (instance && typeof instance.destroy === 'function') {
      try {
        instance.destroy(true);
      } catch (e) {
        console.warn('Error destroying Splide:', e);
      }
    }
    instances.delete(instanceId);
    el.classList.remove('splide-initialized');
    el._splide = null;
  }

  // Optimized mount function
  function mountSplideOn(el, opts) {
    if (!el || typeof Splide === 'undefined') {
      return null;
    }
    safeDestroy(el);
    try {
      const sp = new Splide(el, opts);
      sp.mount();
      const instanceId = el.id || `splide-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      instances.set(instanceId, sp);
      el._splide = sp;
      el.classList.add('splide-initialized');
      return sp;
    } catch (error) {
      return null;
    }
  }

  // Collection slider options
  const collectionSliderOpts = {
    perPage: 2, perMove: 1, rewind: true, gap: '7px', arrows: true, autoplay: true, speed: 200, pagination: false, focus: 'left', padding: { right: '5%' },
    breakpoints: { 700: { perPage: 2, gap: '10px', padding: { right: 0 } }, 480: { perPage: 2, gap: '10px', padding: { right: 0 } } }
  };

  // Product card slider options
  const cardOpts = {
    type: 'slide', perPage: 1, perMove: 1, autoplay: false, arrows: true, pagination: false, gap: '0.5rem', speed: 150, rewind: true, lazyLoad: 'nearby', preloadPages: 1,
    breakpoints: { 768: { arrows: false } }
  };

  /* ----------------------------------------------
   * CORE INITIALIZATION & RE-INITIALIZATION FUNCTIONS (MOVED TO MAIN SCOPE)
   * ---------------------------------------------- */

  function destroyAllSplides(context = document) {
    const cards = context.querySelectorAll(CARD_SELECTOR);
    cards.forEach(safeDestroy);
    const collectionSliders = context.querySelectorAll(COLLECTION_SLIDER_SELECTOR);
    collectionSliders.forEach(safeDestroy);
  }

  function initCollectionSlider(context = document) {
    const collectionSliders = context.querySelectorAll(COLLECTION_SLIDER_SELECTOR);
    if (collectionSliders.length > 0) {
      collectionSliders.forEach((slider, index) => {
        setTimeout(() => {
          mountSplideOn(slider, collectionSliderOpts);
        }, index * 50);
      });
    }
  }

  function initSplidesImmediately(context = document) {
    destroyAllSplides(context);
    initCollectionSlider(context);
    const allCards = context.querySelectorAll(CARD_SELECTOR);
    allCards.forEach((el, index) => {
      setTimeout(() => {
        mountSplideOn(el, cardOpts);
      }, index * 10); 
    });
  }

  function initSplidesLazy(context = document) {
    destroyAllSplides(context);
    initCollectionSlider(context);
    const allCards = context.querySelectorAll(CARD_SELECTOR);
    allCards.forEach((el, index) => {
      if (index < 4) {
        mountSplideOn(el, cardOpts);
      } else {
        setTimeout(() => {
          mountSplideOn(el, cardOpts);
        }, 100 + (index * 20));
      }
    });
  }
  
  // *** THESE WERE MOVED FROM setupCollectionListeners ***
  const reinitSplides = debounce((context) => {
    initSplidesImmediately(context);
  }, DEBOUNCE_TIME);
  
  function handleInfiniteScrollComplete(context) {
    setTimeout(() => {
      initSplidesImmediately(context || document); 
      setTimeout(() => {
        setupAutoInfiniteScroll();
      }, 200);
    }, 500);
  }
  // *******************************************************


  /* ----------------------------------------------
   * INFINITE SCROLL FUNCTIONALITY
   * ---------------------------------------------- */

  async function fetchAndAppendNextPage(loadMoreBtn) {
      const nextUrl = loadMoreBtn.href;

      if (isFetchingNextPage || !nextUrl) {
          return;
      }

      isFetchingNextPage = true;
      loadMoreBtn.classList.add('is-loading'); 
      
      try {
          const response = await fetch(nextUrl);
          const html = await response.text();
          const newDoc = new DOMParser().parseFromString(html, 'text/html');

          const currentProductContainer = document.querySelector(PRODUCT_GRID_CONTAINER_SELECTOR); 
          const currentPaginationWrapper = document.querySelector(PAGINATION_WRAPPER_SELECTOR);

          if (!currentProductContainer) {
              console.error(`Could not find current product container. (Used: ${PRODUCT_GRID_CONTAINER_SELECTOR})`);
              return;
          }
          
          const newProductContainerWrapper = newDoc.querySelector(PRODUCT_GRID_CONTAINER_SELECTOR); 
          const newPaginationWrapper = newDoc.querySelector(PAGINATION_WRAPPER_SELECTOR);
          
          if (!newProductContainerWrapper) {
               console.error(`Could not find product grid in fetched page.`);
               return;
          }
          
          const newProducts = newProductContainerWrapper.querySelectorAll(PRODUCT_ITEM_SELECTOR);
          
          if (newProducts.length > 0) {
              const fragment = document.createDocumentFragment();
              newProducts.forEach(product => fragment.appendChild(product));
              
              currentProductContainer.appendChild(fragment);

              if (currentPaginationWrapper && newPaginationWrapper) {
                  if (infiniteScrollObserver) infiniteScrollObserver.disconnect();
                  currentPaginationWrapper.replaceWith(newPaginationWrapper);
              } else if (newPaginationWrapper) {
                  const collectionDiv = currentProductContainer.closest('.collection');
                  if (collectionDiv) {
                      collectionDiv.appendChild(newPaginationWrapper);
                  }
              }
              
              setTimeout(() => {
                handleInfiniteScrollComplete(fragment);
              }, 100);

          } else {
              if (currentPaginationWrapper && newPaginationWrapper) {
                  if (infiniteScrollObserver) infiniteScrollObserver.disconnect();
                  currentPaginationWrapper.replaceWith(newPaginationWrapper);
              }

              const finalLoadMoreBtn = document.querySelector('[data-infinite-scrolling]');
              if (finalLoadMoreBtn) {
                  finalLoadMoreBtn.classList.add('disabled');
                  finalLoadMoreBtn.style.display = 'none';
                  if (infiniteScrollObserver) infiniteScrollObserver.unobserve(finalLoadMoreBtn);
              }
          }
          
      } catch (error) {
          console.error('Error fetching and appending next page:', error);
      } finally {
          isFetchingNextPage = false;
      }
  }

  function setupAutoInfiniteScroll() {
    if (infiniteScrollObserver) {
      infiniteScrollObserver.disconnect();
    }
    
    const loadMoreBtn = document.querySelector('[data-infinite-scrolling]'); 
    
    if (!loadMoreBtn || loadMoreBtn.classList.contains('disabled') || loadMoreBtn.style.display === 'none') {
      return;
    }

    function fetchAndAppendNextPageWrapper(e) {
        e.preventDefault(); 
        fetchAndAppendNextPage(document.querySelector('[data-infinite-scrolling]'));
    }
    
    loadMoreBtn.removeEventListener('click', fetchAndAppendNextPageWrapper);
    loadMoreBtn.addEventListener('click', fetchAndAppendNextPageWrapper);
    
    infiniteScrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isFetchingNextPage) {
          fetchAndAppendNextPage(loadMoreBtn); 
        }
      });
    }, {
      rootMargin: '500px 0px', 
      threshold: 0
    });
    
    if (!loadMoreBtn.classList.contains('disabled')) {
        infiniteScrollObserver.observe(loadMoreBtn);
    }
  }


  /* ----------------------------------------------
   * EVENT HANDLERS FOR FILTERS/SORTING
   * ---------------------------------------------- */
  
  const setupCollectionListeners = () => {
    const events = [
      'collection:loaded', 'collection:products:loaded', 
      'collection:append', 'products:appended',
      'ajaxContentReplaced', 'filter:updated',
      'sort:changed', 'loadmore:complete', 
      'shopify:section:load'
    ];

    events.forEach(eventName => {
      document.addEventListener(eventName, (e) => {
        const context = e.detail?.container || e.detail?.context || document;
        reinitSplides(context);
        
        if (eventName === 'loadmore:complete' || eventName === 'products:appended') {
          handleInfiniteScrollComplete();
        }
      });
    });

    let currentUrl = window.location.href;
    setInterval(() => {
      if (window.location.href !== currentUrl) {
        currentUrl = window.location.href;
        setTimeout(() => reinitSplides(document), 500);
      }
    }, 100);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          let shouldReinit = false;
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { 
              if (node.matches && (node.matches(CARD_SELECTOR) || node.matches(COLLECTION_SLIDER_SELECTOR) || node.querySelector(CARD_SELECTOR) || node.querySelector(COLLECTION_SLIDER_SELECTOR))) {
                shouldReinit = true;
              }
            }
          });
          if (shouldReinit) {
            reinitSplides(document);
          }
        }
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  };
  
  // ... (Other functions like setupFilterButton, setupProductSizeChartButtons, etc., remain the same) ...

  function setupFilterButton() {
    if (filterButtonInitialized) { return; }
    const filterButton = document.querySelector('.ew-filter-button');
    if (filterButton) {
      document.addEventListener('click', function(e) {
        if (e.target.closest('.ew-filter-button')) {
          e.preventDefault();
          const sidebar = document.querySelector('.ew__sidebar--content');
          if (sidebar) {
            const isActive = sidebar.classList.toggle('ew-sidebar-filter');
            filterButton.textContent = isActive ? 'Hide Filters' : 'Filters';
          }
        }
      });
      filterButtonInitialized = true; 
    }
  }
  function setupProductSizeChartButtons() {
    document.addEventListener('click', function (e) {
      const productSizeChartBtn = e.target.closest('.ew-product-size-chart');
      if (productSizeChartBtn) {
        e.preventDefault();
        let sizeChartPopup = null;
        const productItem = productSizeChartBtn.closest(PRODUCT_ITEM_SELECTOR); 
        if (productItem) {
          sizeChartPopup = productItem.querySelector('.ew-sizechart-popup');
          if (sizeChartPopup) {
            sizeChartPopup.classList.add('active');
            return;
          }
        }
        const cardProduct = productSizeChartBtn.closest('.card-product');
        if (cardProduct) {
          sizeChartPopup = cardProduct.querySelector('.ew-sizechart-popup');
          if (sizeChartPopup) {
            sizeChartPopup.classList.add('active');
            return;
          }
        }
        const allPopups = document.querySelectorAll('.ew-sizechart-popup');
        if (allPopups.length > 0) {
          allPopups[0].classList.add('active');
        } 
      }
    });
  }
  function addToCart(variantId, quantity = 1) {
    return $.post(`${window.routes.root}/cart/add.js`, { id: variantId, quantity });
  }
  function updateSidebarCart(cart) {
    const $dropdown = $('#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper');
    if (!$dropdown.length) return;
    $dropdown.addClass('is-loading').prepend(`
      <div class="loading-overlay loading-overlay--custom">
        <div class="loading-overlay__spinner">
          <svg class="spinner" viewBox="0 0 66 66"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>
        </div>
      </div>
    `);
    $.get(`${window.routes.root}/cart?view=ajax_side_cart`, (data) => {
      $dropdown.removeClass('is-loading').html(data);
      document.body.classList.add('cart-sidebar-show');
    });
  }
  function updatePriceDisplayFromRadio(radio) {
    if (!radio) return;
    const priceEl = radio.closest('.ew-quickview').querySelector('.ew-price-current');
    const compareEl = radio.closest('.ew-quickview').querySelector('.ew-price-compare');
    const price = radio.dataset.variantPriceFormatted || (radio.dataset.variantPrice / 100).toFixed(2);
    const compare = radio.dataset.compareAtPriceFormatted || '';
    if (priceEl) priceEl.innerHTML = price;
    if (compareEl) {
      if (compare && compare !== 'undefined') {
        compareEl.innerHTML = compare;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.ew-quickview-button');
    if (btn) {
      e.preventDefault();
      document.querySelectorAll('.ew-quickview.active').forEach(m => m.classList.remove('active'));
      const card = btn.closest(PRODUCT_ITEM_SELECTOR); 
      const modal = card?.querySelector('.ew-quickview');
      if (modal) modal.classList.add('active');
      return;
    }
    const closeBtn = e.target.closest('.ew-quickview-close');
    if (closeBtn) closeBtn.closest('.ew-quickview')?.classList.remove('active');
  });
  document.addEventListener('change', function (e) {
    const radio = e.target.closest('.ew-size-option input[type="radio"]');
    if (radio) {
      radio.closest('.ew-size-options')?.querySelectorAll('.ew-size-option').forEach(l => l.classList.remove('checked'));
      radio.closest('.ew-size-option')?.classList.add('checked');
      updatePriceDisplayFromRadio(radio);
    }
  });
  document.addEventListener('click', async function (e) {
    const cartBtn = e.target.closest('.ew-add-to-cart');
    if (cartBtn) {
      e.preventDefault();
      const card = cartBtn.closest(PRODUCT_ITEM_SELECTOR); 
      const selected = card.querySelector('.ew-size-option.checked input');
      if (!selected) return alert('Please select a size.');
      const variantId = selected.dataset.variantId || selected.value;
      cartBtn.disabled = true;
      $(cartBtn).addClass('is-loading');
      try {
        await addToCart(variantId, 1);
        const cart = await $.getJSON(`${window.routes.root}/cart.js`);
        updateSidebarCart(cart);
      } catch {
        alert('Could not add to cart.');
      } finally {
        cartBtn.disabled = false;
        $(cartBtn).removeClass('is-loading');
      }
    }
  });
  document.addEventListener('click', function (e) {
    const sizeChartBtn = e.target.closest('.ew-open-sizechart');
    if (sizeChartBtn) {
      e.preventDefault();
      const quickview = sizeChartBtn.closest('.ew-quickview');
      if (quickview) {
        const popupInQuickview = quickview.querySelector('.ew-sizechart-popup');
        if (popupInQuickview) {
          popupInQuickview.classList.add('active');
          return;
        }
      }
      const productItem = sizeChartBtn.closest(PRODUCT_ITEM_SELECTOR); 
      if (productItem) {
        const popupInProduct = productItem.querySelector('.ew-sizechart-popup');
        if (popupInProduct) {
          popupInProduct.classList.add('active');
          return;
        }
      }
      const cardProduct = sizeChartBtn.closest('.card-product');
      if (cardProduct) {
        const popupInCard = cardProduct.querySelector('.ew-sizechart-popup');
        if (popupInCard) {
          popupInCard.classList.add('active');
          return;
        }
      }
      const allPopups = document.querySelectorAll('.ew-sizechart-popup');
      if (allPopups.length > 0) {
        allPopups[0].classList.add('active');
      } 
    }
  });
  document.addEventListener('click', function (e) {
    if (e.target.matches('.ew-sizechart-close, .ew-sizechart-popup__overlay')) {
      const popup = e.target.closest('.ew-sizechart-popup');
      if (popup) {
        popup.classList.remove('active');
      }
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      document.querySelectorAll('.ew-quickview.active').forEach(modal => {
        modal.classList.remove('active');
      });
      document.querySelectorAll('.ew-sizechart-popup.active').forEach(modal => {
        modal.classList.remove('active');
      });
    }
  });

  /* ----------------------------------------------
   * INITIALIZATION
   * ---------------------------------------------- */
  
  function initialize() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          initSplidesLazy(document);
          setupCollectionListeners();
          setupAutoInfiniteScroll(); 
          setupFilterButton(); 
          setupProductSizeChartButtons(); 
        }, 100);
      });
    } else {
      setTimeout(() => {
        initSplidesLazy(document);
        setupCollectionListeners();
        setupAutoInfiniteScroll(); 
        setupFilterButton(); 
        setupProductSizeChartButtons(); 
      }, 100);
    }
  }

  document.addEventListener('DOMContentLoaded', setupAutoInfiniteScroll);
  document.addEventListener('collection:loaded', setupAutoInfiniteScroll);
  document.addEventListener('ajaxContentReplaced', setupAutoInfiniteScroll);

  // Start everything
  initialize();

})();